import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { FieldPath, SubmitHandler } from 'react-hook-form';
import { Link, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal, unmaskNumber } from '@givebutter/util';
import {
    faDollarCircle,
    faEnvelope,
    faExclamationTriangle,
    faEye,
    faLink,
    faShare,
} from '@fortawesome/pro-regular-svg-icons';
import {
    faChevronDown,
    faEdit,
    faEllipsisV,
    faGift,
    faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchFunds } from 'api/funds';
import { attachTransaction, editPledge, editPledgeInternalNote } from 'api/pledges';
import { EditInstallments } from 'assets/js/pages/pledges/EditInstallments';
import {
    InstallmentProgress,
    InstallmentProgressItem,
    PledgeWithInstallmentProgress,
} from 'assets/js/pages/pledges/InstallmentProgress';
import { PaymentItem } from 'assets/js/pages/pledges/PaymentItem';
import * as PledgeActions from 'assets/js/pages/pledges/PledgeActions';
import { PledgeSettingsForm } from 'assets/js/pages/pledges/PledgeSettingsForm';
import { PledgeStatusIndicator } from 'assets/js/pages/pledges/PledgeStatusIndicator';
import { SelectCampaign } from 'assets/js/pages/pledges/SelectCampaign';
import { SelectTransaction } from 'assets/js/pages/pledges/SelectTransaction';
import * as SendEmailPaymentLinkDialog from 'assets/js/pages/pledges/SendEmailPaymentLinkDialog';
import { TabButton } from 'assets/js/pages/pledges/TabButton';
import { useCurrentPledge } from 'assets/js/pages/pledges/useCurrentPledge';
import { useInstallmentCalculation } from 'assets/js/pages/pledges/useInstallmentCalculation';
import {
    InstallmentStatus,
    PledgeFormValue,
    usePledgeForm,
} from 'assets/js/pages/pledges/usePledgeForm';
import { SupporterDetails } from 'assets/js/pages/transactions/SupporterDetails';
import * as Drawer from 'components/drawer';
import { DrawerIncrementor } from 'components/drawer/DrawerIncrementor';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import * as Input from 'components/input';
import * as Select from 'components/select';
import { useCopy } from 'components/snippet';
import { EmptyData } from 'components/util';
import { FUNDS_PARAMS } from 'constants/params';
import { InstallmentFrequency, PledgeStatus, PledgeType } from 'constants/pledges';
import { CurrencySymbol, useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateFromServer, dateToServerDateTime, isoDateFromServer } from 'helpers/dates';
import { isEqual } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { IndexedTransaction, Transaction } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const ViewPledgeDrawer = () => {
    const { pledge_id } = useParams<{
        pledge_id: string;
    }>();
    const pledgeId = parseInt(pledge_id);
    const { accountId } = useCurrentAccount();
    const currency = useCurrencySymbol();
    const toast = useToast();
    const queryClient = useQueryClient();
    const [editField, setEditField] = useState<FieldPath<PledgeFormValue>>();
    const [showAddTransaction, setShowAddTransaction] = useState(false);
    const [showIncorrectAmount, setShowIncorrectAmount] = useState(false);
    const { onClickCopy } = useCopy();
    const { state } = useLocation<{ activeTab: string }>();
    const [tab, setTab] = useState(state?.activeTab ?? 'installments');
    const match = useRouteMatch();
    const isIndex = match.path.includes('/accounts/:account_id/pledges');

    const { data: fundData } = useSuspenseQuery({
        queryKey: accountQueryKeys.fundsWithFilters(accountId, FUNDS_PARAMS),
        queryFn: () => fetchFunds(accountId, FUNDS_PARAMS),
    });
    const funds = fundData.data;

    const { data, isPending } = useCurrentPledge();

    const missedInstallments = useMemo(
        () => data.installments.filter(item => item.status === InstallmentStatus.Late),
        [data.installments],
    );

    const methods = usePledgeForm({
        defaultValues: {
            ...data,
            first_installment_date: isoDateFromServer(data.first_installment_date),
            fulfilled_at: isoDateFromServer(data.first_installment_date),
            campaign: [
                {
                    label: data.campaign.title,
                    id: `${data.campaign.id}`,
                },
            ],
            fund: [
                {
                    label: data.fund.name,
                    id: `${data.fund.id}`,
                },
            ],
            number_of_installments: data.installments.length,
            installments: data.installments,
        },
        shouldUnregister: false,
    });

    const { getInstallmentFormItems } = useInstallmentCalculation(methods);

    const {
        setError,
        control,
        path,
        handleSubmit,
        resetField,
        getValues,
        clearErrors,
        watch,
        resetFormButton,
        setValue,
    } = methods;

    const [installments, total, memo, internalNote] = watch([
        'installments',
        'amount',
        'memo',
        'internal_note',
    ]);

    const installmentsRef = useRef<InstallmentProgressItem[]>(data.installments);

    const memoizedInstallments = useMemo(() => {
        const prevInstallments = installmentsRef.current.map(({ date, amount }) => ({
            date: dateToServerDateTime(date.endOf('day')),
            amount: unmaskNumber(amount),
        }));
        const equal = isEqual(
            prevInstallments,
            data.installments.map(item => ({
                date: dateToServerDateTime(item.date.endOf('day')),
                amount: unmaskNumber(item.amount),
            })),
        );

        if (!equal) {
            installmentsRef.current = data.installments;
            return data.installments;
        }

        return installmentsRef.current;
    }, [data.installments]);

    useEffect(() => {
        setValue(
            'installments',
            getInstallmentFormItems(memoizedInstallments, data.amount_fulfilled),
        );
    }, [data.amount_fulfilled, memoizedInstallments, getInstallmentFormItems, setValue]);

    const invalidateQueries = () => {
        queryClient.invalidateQueries({
            queryKey: accountQueryKeys.pledges(accountId),
        });
        queryClient.invalidateQueries({
            queryKey: transactionQueryKeys.transactions(accountId),
            type: 'all',
        });
    };

    const { mutate: submit } = useMutation({
        mutationFn: (form: PledgeFormValue) => {
            if (data.status_friendly === PledgeStatus.Canceled) {
                return editPledgeInternalNote(accountId, pledgeId, form.internal_note);
            }

            const installments = form.installments.map(({ date, amount }) => ({
                date: dateToServerDateTime(date.endOf('day')),
                amount: unmaskNumber(amount),
            }));
            const changedInstallments = !isEqual(
                data.installments.map(item => dateToServerDateTime(item.date.endOf('day'))),
                installments.map(item => item.date),
            );
            const formData = {
                ...data,
                ...form,
                id: pledgeId,
                contact_id: data.contact?.id,
                installment_frequency: changedInstallments
                    ? InstallmentFrequency.Custom
                    : data.installment_frequency,
                campaign_id: form.campaign?.[0]?.id ? parseInt(form.campaign[0].id) : undefined,
                fund_id: form.fund?.[0]?.id ? parseInt(form.fund[0].id) : null,
                first_installment_date: dateToServerDateTime(
                    form.first_installment_date.endOf('day'),
                ),
                fulfilled_at: dateToServerDateTime(form.fulfilled_at.endOf('day')),
                installments,
                amount: unmaskNumber(form.amount),
            };
            return editPledge(accountId, formData);
        },
        onSuccess: () => {
            toast.success('Pledge edited successfully!');
            invalidateQueries();
            if (editField) {
                resetField(editField, { defaultValue: getValues(editField) });
                setEditField(undefined);
            }
        },
        onError: (error: GivebutterAxiosError) => {
            resetFormButton();
            const errors = error.response?.data.errors;
            toast.error(error.response?.data?.message ?? 'An unknown error occurred.');
            if (errors) {
                Object.keys(errors).forEach(errorKey => {
                    const key =
                        {
                            campaign_id: 'campaign',
                            fund_id: 'fund',
                        }[errorKey] ?? errorKey;

                    const error = errors[errorKey][0];

                    const message = error.replace(
                        /installments\.\d+\.amount/g,
                        'installment amount',
                    );

                    setError(key as FieldPath<PledgeFormValue>, {
                        message,
                    });
                });
            }
        },
    });

    const installmentTotal = installments.reduce(
        (all, item) => all + unmaskNumber(item?.amount || '0'),
        0,
    );
    const incorrectAmount = parseFloat(installmentTotal.toFixed(2)) !== unmaskNumber(total);

    useEffect(() => {
        setShowIncorrectAmount(incorrectAmount);
    }, [incorrectAmount]);

    const installmentsValidationMessage = useMemo(() => {
        const installmentsAmount = parseFloat(installmentTotal.toFixed(2));
        const amount = total - installmentsAmount;
        const amountFormatted = (
            <span className="font-semibold">
                <CurrencySymbol />
                {maskDecimal(Math.abs(amount))}
            </span>
        );
        const modificationMessage =
            amount < 0 ? <>subtract {amountFormatted} from</> : <>add {amountFormatted} to</>;
        return (
            <>
                Please {modificationMessage} the installments or set the pledge amount to{' '}
                <span className="font-semibold">
                    <CurrencySymbol />
                    {maskDecimal(installmentsAmount)}
                </span>
            </>
        );
    }, [installmentTotal, total]);

    const cancelEdit = () => {
        if (editField) {
            if (editField === 'installments') {
                resetField('installments', {
                    defaultValue: getInstallmentFormItems(data.installments, data.amount_fulfilled),
                });
            } else {
                resetField(editField);
            }
            clearErrors(editField);
        }
        setEditField(undefined);
    };

    const frequencyLabel = useMemo(() => {
        if (data?.installment_frequency === InstallmentFrequency.OneTime) {
            return 'One-time';
        }
        const label = {
            [InstallmentFrequency.Annually]: 'Annual',
            [InstallmentFrequency.Monthly]: 'Monthly',
            [InstallmentFrequency.Quarterly]: 'Quarterly',
            [InstallmentFrequency.SemiAnnually]: 'Semi-annual',
            [InstallmentFrequency.Weekly]: 'Weekly',
            [InstallmentFrequency.Custom]: 'Custom',
        }[data.installment_frequency];

        return `${label} installments`;
    }, [data.installment_frequency]);

    const pledgeType = useMemo(() => {
        return {
            [PledgeType.Donation]: 'Donation',
            [PledgeType.Match]: 'Match',
            [PledgeType.Payroll]: 'Payroll',
            [PledgeType.Other]: 'Other',
            [PledgeType.Sponsorship]: 'Sponsorship',
        }[data.type];
    }, [data.type]);

    const { mutate: addTransaction, isPending: isAdding } = useMutation({
        mutationFn: (transaction_id: number) => {
            return attachTransaction(accountId, pledgeId, { transaction_id });
        },
        onSuccess: () => {
            setShowAddTransaction(false);
            invalidateQueries();
        },
    });

    return (
        <Form.Provider {...methods}>
            <Drawer.Header>
                <div className="flex w-full flex-col gap-4">
                    <div className={'flex justify-between'}>
                        <BoxIcon size="lg">
                            <FontAwesomeIcon size={'lg'} icon={faGift} />
                        </BoxIcon>
                        <div className="flex items-center gap-2">
                            {isIndex && <DrawerIncrementor resource="pledges" />}

                            <PledgeActions.Root pledge={data}>
                                <PledgeActions.Trigger asChild>
                                    <Button
                                        size={'sm'}
                                        variant={'outline'}
                                        color={'neutral'}
                                        shape={'square'}
                                    >
                                        <FontAwesomeIcon icon={faEllipsisV} />
                                    </Button>
                                </PledgeActions.Trigger>
                            </PledgeActions.Root>
                        </div>
                    </div>
                    <div>
                        <div className={'flex items-center gap-2'}>
                            <p className={'text-2xl font-medium'}>
                                {currency}
                                {maskDecimal(data?.amount || 0)}
                            </p>
                            {!!data?.status_friendly && (
                                <PledgeStatusIndicator value={data.status_friendly}>
                                    {data.status_friendly === PledgeStatus.Fulfilled &&
                                        data?.fulfilled_at &&
                                        isoDateFromServer(data?.fulfilled_at).toFormat(
                                            'LLLL dd, yyyy',
                                        )}
                                </PledgeStatusIndicator>
                            )}
                        </div>
                        <p className={'text-sm text-gray-600'}>{frequencyLabel}</p>
                        {data.last_reminded_at && (
                            <p className={'text-sm text-gray-600'}>
                                Last reminder sent:{' '}
                                {dateFromServer(data.last_reminded_at).toFormat('MM/dd/yy')}
                            </p>
                        )}
                    </div>
                </div>
            </Drawer.Header>
            <Drawer.Body className="flex flex-col gap-6">
                <form
                    className={'contents'}
                    onSubmit={handleSubmit(submit as SubmitHandler<PledgeFormValue>)}
                >
                    {!!missedInstallments.length && data.contact && (
                        <div className="bg-orange-25 relative flex gap-3 rounded-lg border border-orange-300 p-3">
                            <FontAwesomeIcon
                                className="size-5 text-orange-500"
                                icon={faExclamationTriangle}
                            />
                            <div className="text-sm text-orange-700">
                                <p className="font-medium">
                                    {data.contact.first_name} missed{' '}
                                    {missedInstallments.length > 1
                                        ? 'multiple installments'
                                        : 'an installment'}
                                </p>
                                <p>
                                    To keep them on schedule, send a reminder or reach out so that
                                    you can adjust their pledge installments as needed.
                                </p>
                            </div>
                        </div>
                    )}

                    {data.status_friendly !== PledgeStatus.Canceled && (
                        <div className="flex flex-col gap-6">
                            {data.contact && <SupporterDetails contact={data.contact} />}
                            <div className="flex flex-col gap-2">
                                <div className="flex justify-between text-sm">
                                    <div className="font-medium text-gray-900">
                                        Pledge payment portal
                                    </div>
                                    <a
                                        href={data.pay_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FontAwesomeIcon icon={faEye} /> View
                                    </a>
                                </div>
                                <Form.Container className="flex flex-row gap-0">
                                    <Input.Root className="input-left">
                                        <Input.Form value={data.pay_url} readOnly />
                                    </Input.Root>
                                    <DropdownMenu.Root>
                                        <DropdownMenu.Trigger
                                            asChild
                                            onClick={e => e.preventDefault()}
                                        >
                                            <Button
                                                variant="outline"
                                                color="neutral"
                                                className="input-right"
                                            >
                                                <FontAwesomeIcon icon={faShare} />{' '}
                                                <span className="text-sm">Share link</span>{' '}
                                                <FontAwesomeIcon icon={faChevronDown} />
                                            </Button>
                                        </DropdownMenu.Trigger>
                                        <DropdownMenu.Portal>
                                            <DropdownMenu.Content sideOffset={4} align={'end'}>
                                                <SendEmailPaymentLinkDialog.Root>
                                                    <SendEmailPaymentLinkDialog.Trigger asChild>
                                                        <DropdownMenu.Item
                                                            onSelect={e => e.preventDefault()}
                                                            className="flex items-center gap-2"
                                                        >
                                                            <FontAwesomeIcon icon={faEnvelope} />
                                                            Send email
                                                        </DropdownMenu.Item>
                                                    </SendEmailPaymentLinkDialog.Trigger>
                                                </SendEmailPaymentLinkDialog.Root>
                                                <DropdownMenu.Item
                                                    onClick={e => {
                                                        onClickCopy(
                                                            e as unknown as React.MouseEvent<
                                                                HTMLButtonElement,
                                                                MouseEvent
                                                            >,
                                                            data.pay_url,
                                                        );
                                                        toast.success('Link copied');
                                                    }}
                                                    className="flex items-center gap-2"
                                                >
                                                    <FontAwesomeIcon icon={faLink} />
                                                    Copy link
                                                </DropdownMenu.Item>
                                            </DropdownMenu.Content>
                                        </DropdownMenu.Portal>
                                    </DropdownMenu.Root>
                                </Form.Container>
                                {data.contact && (
                                    <p className="text-sm text-gray-500">
                                        Share this link with {data.contact.first_name} so they can
                                        pay towards their pledge.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex gap-2 border-b border-b-gray-200">
                        <TabButton
                            active={tab === 'installments'}
                            onClick={() => setTab('installments')}
                        >
                            Installments
                        </TabButton>
                        <TabButton active={tab === 'payments'} onClick={() => setTab('payments')}>
                            Payments
                        </TabButton>
                        <TabButton active={tab === 'settings'} onClick={() => setTab('settings')}>
                            Settings
                        </TabButton>
                        <TabButton active={tab === 'note'} onClick={() => setTab('note')}>
                            Note
                        </TabButton>
                    </div>

                    {tab === 'installments' && (
                        <>
                            <div className="flex flex-col gap-6 rounded-lg bg-gray-50 p-4">
                                <div className="flex justify-between gap-6">
                                    {editField === 'installments' && (
                                        <Form.Container>
                                            <Form.Label name={path('amount')}>
                                                Pledge amount <Form.LabelAsterisk />
                                            </Form.Label>
                                            <Form.TextInputRoot
                                                control={control}
                                                name="amount"
                                                inputRootProps={{
                                                    mask: 'decimal',
                                                }}
                                            >
                                                <Input.CurrencyIcon />
                                                <Form.TextInputForm />
                                            </Form.TextInputRoot>
                                            <Form.Error name={path('amount')} />
                                        </Form.Container>
                                    )}
                                    {editField !== 'installments' && (
                                        <div className="grid flex-1 grid-cols-2 gap-6">
                                            <div className="flex flex-col">
                                                <div className="text-sm font-medium text-gray-500">
                                                    Paid
                                                </div>
                                                <div className="text-lg font-semibold">
                                                    {currency}
                                                    {maskDecimal(data.amount_fulfilled)}
                                                </div>
                                            </div>
                                            <div className="flex flex-col">
                                                <div className="text-sm font-medium text-gray-500">
                                                    Balance
                                                </div>
                                                <div className="text-lg font-semibold">
                                                    <CurrencySymbol />
                                                    {maskDecimal(data.amount_remaining)}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {data.status_friendly !== PledgeStatus.Canceled && (
                                        <div className="self-end justify-self-end">
                                            {editField === 'installments' ? (
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="outline"
                                                        color="neutral"
                                                        onClick={() => cancelEdit()}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        type="submit"
                                                        variant="solid"
                                                        color="primary"
                                                    >
                                                        Save
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button
                                                    variant="none"
                                                    color="primary"
                                                    size="sm"
                                                    className="font-medium"
                                                    onClick={() => {
                                                        setEditField('installments');
                                                    }}
                                                >
                                                    Edit schedule
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="border-t border-t-gray-200 pt-4">
                                    {editField === 'installments' ? (
                                        <EditInstallments />
                                    ) : (
                                        <InstallmentProgress installments={data.installments} />
                                    )}
                                </div>
                            </div>
                            {showIncorrectAmount && editField === 'installments' && (
                                <div className="relative flex gap-4 rounded-lg border border-gray-200 p-4 pr-8">
                                    <div className="flex size-10 items-center justify-center rounded-md border border-gray-200">
                                        <FontAwesomeIcon
                                            className="text-red-500"
                                            icon={faExclamationTriangle}
                                        />
                                    </div>
                                    <div className="flex flex-1 justify-between">
                                        <div>
                                            <p className="font-semibold">
                                                Your pledge does not add up to <CurrencySymbol />
                                                {maskDecimal(total)}
                                            </p>
                                            <p>{installmentsValidationMessage}</p>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="transparent"
                                            onClick={() => setShowIncorrectAmount(false)}
                                            className="absolute right-1 top-1 !text-gray-500"
                                            aria-label="Close"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {tab === 'payments' && (
                        <div className="flex flex-col">
                            <div className="flex items-center justify-between">
                                <p className="text-lg font-medium text-gray-900">History</p>
                                <div className="flex gap-1">
                                    <DropdownMenu.Root>
                                        <DropdownMenu.Trigger asChild={true}>
                                            <Button
                                                size={'sm'}
                                                variant={'outline'}
                                                color={'neutral'}
                                            >
                                                Link transaction
                                                <FontAwesomeIcon icon={faLink} />
                                            </Button>
                                        </DropdownMenu.Trigger>
                                        <DropdownMenu.Portal>
                                            <DropdownMenu.Content sideOffset={4} align={'end'}>
                                                <DropdownMenu.LinkItem
                                                    to={`/accounts/${accountId}/pledges/${pledgeId}/transaction`}
                                                >
                                                    <p className="flex items-center gap-2">
                                                        <FontAwesomeIcon icon={faDollarCircle} />
                                                        Link new transaction
                                                    </p>
                                                </DropdownMenu.LinkItem>
                                                <DropdownMenu.Item
                                                    className="flex items-center gap-2"
                                                    onSelect={() => {
                                                        setShowAddTransaction(true);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faDollarCircle} /> Link
                                                    existing transaction
                                                </DropdownMenu.Item>
                                            </DropdownMenu.Content>
                                        </DropdownMenu.Portal>
                                    </DropdownMenu.Root>
                                </div>
                            </div>

                            <div className="relative flex flex-col gap-4">
                                {showAddTransaction && (
                                    <div className="flex w-full">
                                        <Suspense
                                            fallback={
                                                <div className="flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white/50">
                                                    <Loader variant="static" />
                                                </div>
                                            }
                                        >
                                            <Form.Label
                                                className="sr-only"
                                                name="existingTransaction"
                                            >
                                                Transaction
                                            </Form.Label>
                                            <SelectTransaction
                                                onSelectItem={(selected: unknown) => {
                                                    const { value } =
                                                        selected as Select.SelectItemWithLabel<IndexedTransaction>;
                                                    value?.id && addTransaction(value.id);
                                                }}
                                            />
                                        </Suspense>
                                        <Button
                                            type="button"
                                            variant="transparent"
                                            shape={'square'}
                                            onClick={() => setShowAddTransaction(false)}
                                            aria-label="Remove"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </Button>
                                    </div>
                                )}

                                {data.transactions?.length ? (
                                    <div>
                                        {data.transactions.map(item => (
                                            <PaymentItem key={item.id} item={item as Transaction} />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center gap-4">
                                        <div className="flex size-12 items-center justify-center rounded-lg border border-gray-200">
                                            <FontAwesomeIcon
                                                className="text-2xl text-purple-600"
                                                icon={faDollarCircle}
                                            />
                                        </div>
                                        <div className="flex flex-col items-center">
                                            <p className="font-medium">No payments received</p>
                                            <p className="text-gray-600">
                                                Share the payment link or add a transaction{' '}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {(isAdding || isPending) && (
                                    <div className="absolute inset-0 bg-white bg-opacity-50">
                                        <Loader />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {tab === 'settings' && (
                        <div className="grid grid-cols-2 gap-x-4 gap-y-6">
                            <div className="flex flex-col gap-1">
                                <div className="text-sm font-medium">Campaign credited</div>
                                <div className="flex items-center">
                                    {editField === 'campaign' ? (
                                        <div className="flex w-full flex-col items-end gap-2">
                                            <Form.Container>
                                                <SelectCampaign />
                                                <Form.Error name={path('campaign')} />
                                            </Form.Container>
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    color="neutral"
                                                    onClick={() => cancelEdit()}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    type="submit"
                                                    variant="solid"
                                                    color="primary"
                                                >
                                                    Save
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            {data.campaign.title ? (
                                                <Link
                                                    to={`/accounts/${accountId}/campaigns/${data.campaign.id}`}
                                                >
                                                    {data.campaign.title}
                                                </Link>
                                            ) : (
                                                <EmptyData />
                                            )}

                                            <EditFieldButton
                                                pledge={data}
                                                onEdit={() => setEditField('campaign')}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1">
                                <div className="text-sm font-medium">Fund</div>
                                <div className="flex items-center">
                                    {editField === 'fund' ? (
                                        <div className="flex w-full flex-col items-end gap-2">
                                            <Form.Container>
                                                <Select.UseFormSearchableSelect
                                                    clearable
                                                    control={control}
                                                    name={'fund'}
                                                    multi={false}
                                                >
                                                    <Select.ListItem
                                                        value={{ label: 'None', id: '' }}
                                                    >
                                                        None
                                                    </Select.ListItem>
                                                    {funds.map(fund => (
                                                        <Select.ListItem
                                                            key={fund.id}
                                                            value={{
                                                                id: fund.id.toString(),
                                                                label: fund.name,
                                                            }}
                                                        >
                                                            {fund.name}
                                                        </Select.ListItem>
                                                    ))}
                                                </Select.UseFormSearchableSelect>
                                                <Form.Error name={path('fund')} />
                                            </Form.Container>
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    color="neutral"
                                                    onClick={() => cancelEdit()}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    type="submit"
                                                    variant="solid"
                                                    color="primary"
                                                >
                                                    Save
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <span>{data.fund.name ?? <EmptyData />}</span>

                                            <EditFieldButton
                                                pledge={data}
                                                onEdit={() => setEditField('fund')}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1">
                                <div className="text-sm font-medium">Pledge type</div>
                                <div className="flex items-center">
                                    {editField === 'type' ? (
                                        <div className="flex w-full flex-col items-end gap-2">
                                            <Form.Container>
                                                <Form.SingleSelect control={control} name="type">
                                                    <SingleSelect.Trigger aria-label="Pledge type" />
                                                    <SingleSelect.Content>
                                                        <SingleSelect.Item
                                                            value={PledgeType.Donation}
                                                        >
                                                            <SingleSelect.ItemText>
                                                                Donation
                                                            </SingleSelect.ItemText>
                                                        </SingleSelect.Item>
                                                        <SingleSelect.Item
                                                            value={PledgeType.Sponsorship}
                                                        >
                                                            <SingleSelect.ItemText>
                                                                Sponsorship
                                                            </SingleSelect.ItemText>
                                                        </SingleSelect.Item>
                                                        <SingleSelect.Item value={PledgeType.Match}>
                                                            <SingleSelect.ItemText>
                                                                Match
                                                            </SingleSelect.ItemText>
                                                        </SingleSelect.Item>
                                                        <SingleSelect.Item
                                                            value={PledgeType.Payroll}
                                                        >
                                                            <SingleSelect.ItemText>
                                                                Payroll
                                                            </SingleSelect.ItemText>
                                                        </SingleSelect.Item>
                                                        <SingleSelect.Item value={PledgeType.Other}>
                                                            <SingleSelect.ItemText>
                                                                Other
                                                            </SingleSelect.ItemText>
                                                        </SingleSelect.Item>
                                                    </SingleSelect.Content>
                                                </Form.SingleSelect>

                                                <Form.Error name={path('type')} />
                                            </Form.Container>
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    color="neutral"
                                                    onClick={() => cancelEdit()}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    type="submit"
                                                    variant="solid"
                                                    color="primary"
                                                >
                                                    Save
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <span>{pledgeType ?? <EmptyData />}</span>

                                            <EditFieldButton
                                                pledge={data}
                                                onEdit={() => setEditField('type')}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="col-span-2 flex flex-col gap-1">
                                <div className="text-sm font-medium">Memo</div>
                                <div className="flex items-center">
                                    {editField === 'memo' ? (
                                        <div className="flex w-full flex-col items-end gap-2">
                                            <Form.Container>
                                                <Form.Label className="sr-only" name="memo">
                                                    Memo
                                                </Form.Label>
                                                <Form.UseFormTextArea
                                                    control={control}
                                                    name="memo"
                                                    maxLength={600}
                                                    placeholder="Include a personalized message to your donor"
                                                    rows={9}
                                                />
                                                <TextareaLimit value={memo} />
                                                <Form.Error name={path('memo')} />
                                            </Form.Container>
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    color="neutral"
                                                    onClick={() => cancelEdit()}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    type="submit"
                                                    variant="solid"
                                                    color="primary"
                                                >
                                                    Save
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="max-w-full">
                                            <p className="whitespace-pre-line break-words text-gray-600">
                                                {data.memo ?? <EmptyData />}
                                            </p>
                                            {data.status_friendly !== PledgeStatus.Canceled && (
                                                <Button
                                                    variant="none"
                                                    color="primary"
                                                    size="xs"
                                                    className="font-medium"
                                                    onClick={() => setEditField('memo')}
                                                >
                                                    Edit memo
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="col-span-2">
                                <PledgeSettingsForm
                                    onChange={handleSubmit(
                                        submit as SubmitHandler<PledgeFormValue>,
                                    )}
                                />
                            </div>
                        </div>
                    )}

                    {tab === 'note' && (
                        <Form.Container>
                            <Form.Label name="internal_note">Internal note</Form.Label>
                            {editField === 'internal_note' ? (
                                <div className="flex flex-col items-end">
                                    <div className="flex w-full flex-col gap-2">
                                        <Form.UseFormTextArea
                                            control={control}
                                            name="internal_note"
                                            maxLength={600}
                                            placeholder="Add a note for internal use."
                                            rows={9}
                                        />
                                        <TextareaLimit value={internalNote} />
                                        <Form.Error name={path('internal_note')} />
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            color="neutral"
                                            onClick={() => cancelEdit()}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            size="sm"
                                            type="submit"
                                            variant="solid"
                                            color="primary"
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="max-w-full">
                                    <p className="whitespace-pre-line break-words text-gray-600">
                                        {data.internal_note ?? <EmptyData />}
                                    </p>
                                    <Button
                                        variant="none"
                                        color="primary"
                                        size="xs"
                                        className="font-medium"
                                        onClick={() => setEditField('internal_note')}
                                    >
                                        Edit
                                    </Button>
                                </div>
                            )}
                        </Form.Container>
                    )}
                </form>
            </Drawer.Body>
        </Form.Provider>
    );
};

const EditFieldButton = ({
    pledge,
    onEdit,
}: {
    pledge: PledgeWithInstallmentProgress;
    onEdit: () => void;
}) => {
    return (
        pledge.status_friendly !== PledgeStatus.Canceled && (
            <Button variant="none" size="sm" shape={'square'} onClick={onEdit} aria-label="Edit">
                <FontAwesomeIcon className="text-gray-400" icon={faEdit} />
            </Button>
        )
    );
};

const TextareaLimit = ({ value, limit = 600 }: { value?: string; limit?: number }) => (
    <span className="text-xs text-gray-500">
        {value && value?.length > 0 ? value.length : 0}/{limit} characters
    </span>
);

export { ViewPledgeDrawer };
